import { Link, useLocation } from 'react-router-dom';
import { LAUNCHED_STATES, stateSlugToDisplay } from '../../lib/stateCore.mjs';
import type { StateSlug } from '../../lib/stateCore.mjs';
import { buildStateDirectoryRootUrl } from '../../lib/stateRoutes';

/**
 * The state pills on a DIRECTORY ROOT page (UI-57 feature slice, operator-ruled
 * 2026-08-13): each launched state renders as a pill; the current state is inert
 * with aria-current="page"; every OTHER state is a real <a> that TRAVELS to that
 * state's directory root — ruling 3 as reshaped by the operator ("a visible control
 * that travels to real per-state addresses"), which is also what makes the two
 * state directories crawlably interlinked once prerendered.
 *
 * ⚠️ DORMANT UNTIL THE FLIP: the sole renderer is DirectoryPage's state-root mode,
 * gated on stateDirectoryRootSlug(pathname), which is null on every route that
 * exists today (see lib/stateRoutes.ts). Nothing visible changes until the hub
 * vacates '/{state}/gyms'.
 *
 * FILTER CARRY (operator-ruled): styles/age/exp travel across a state switch —
 * they mean the same thing in both states. PLACE does not: suburb, map position
 * and radius all reset, because a NSW suburb has no VIC equivalent and a radius
 * without its anchor is a stale preference. The carried set is exported so the
 * guard asserts the ruling, not an incidental implementation detail.
 *
 * COPY: zero new strings. The group label reuses the homepage's shipped
 * "Choose a state to browse"; pill text is stateSlugToDisplay's existing
 * vocabulary. Pill styling mirrors the homepage pills (44px target, ring-offset
 * focus, border on BOTH variants so the two share a box model — see
 * LandingPage's pill comment for why).
 */
export const STATE_SWITCH_CARRIED_PARAMS = ['styles', 'age', 'exp'] as const;

/**
 * Target address for switching to `state`, carrying only the ruled filter params
 * from `search`. Pure. Single-'?' by construction — one URLSearchParams, never
 * string-appended (the withAgeQuery lesson: '/gyms?styles=BJJ?age=…' parses as ONE
 * param and silently drops both filters while looking correct in a link).
 */
export function buildStateSwitchUrl(state: StateSlug, search: string): string {
    const current = new URLSearchParams(search);
    const carried = new URLSearchParams();
    for (const key of STATE_SWITCH_CARRIED_PARAMS) {
        const value = current.get(key);
        if (value !== null && value !== '') carried.set(key, value);
    }
    const qs = carried.toString();
    const root = buildStateDirectoryRootUrl(state);
    return qs ? `${root}?${qs}` : root;
}

const pillBase =
    'inline-flex items-center justify-center min-h-[44px] px-5 rounded-full border text-sm font-medium ' +
    'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ' +
    'focus-visible:ring-offset-2';

/**
 * Pure-props core, so tests can exercise a THREE-state future without mutating
 * LAUNCHED_STATES (the SITE-6 class: a new state must add a pill with no code
 * change here). App code should use the default export.
 */
export function StateDirectorySwitchFor({
    states,
    currentState,
}: {
    states: readonly StateSlug[];
    /** null = CHOOSER mode (the /gyms page): no state is current, every pill travels. */
    currentState: StateSlug | null;
}) {
    const { search } = useLocation();
    return (
        // ⭐ A FULL-WIDTH BAR, SPLIT EVENLY (operator-ruled 2026-08-16). `w-full` + `flex-1`
        // on every child rather than a two-column grid: `grid-cols-2` would encode the
        // CURRENT number of states as a layout constant and silently mis-split the day a
        // third launches — the same literal-vs-derived trap this component was written to
        // avoid, one layer down in the CSS. flex-1 divides whatever there is, evenly, for
        // any N. Order follows LAUNCHED_STATES, which puts NSW left and VIC right.
        <nav aria-label="Choose a state to browse" className="flex w-full gap-3 mb-6">
            {states.map((slug) =>
                slug === currentState ? (
                    // ⚠️ UNREACHABLE IN PRODUCTION SINCE 2026-08-16 and kept deliberately.
                    // The pills were removed from the state directories, so the only caller
                    // is the /gyms chooser, which passes currentState={null} — no pill is
                    // ever "current" there. This branch is the component's contract and is
                    // still exercised by its tests; deleting it would make a future
                    // re-introduction silently lose the aria-current semantics.
                    <span
                        key={slug}
                        aria-current="page"
                        className={`${pillBase} flex-1 bg-brand-primary text-white border-transparent`}
                    >
                        {stateSlugToDisplay(slug)}
                    </span>
                ) : (
                    // ⚠️ NO BRAND-COLOUR FAVOURING (operator-ruled): both states render in
                    // the identical neutral treatment, so neither reads as the default or
                    // the recommended one. On the chooser this is every pill.
                    <Link
                        key={slug}
                        to={buildStateSwitchUrl(slug, search)}
                        className={`${pillBase} flex-1 bg-surface-base text-text-primary border-border-default hover:border-text-subtle`}
                    >
                        {stateSlugToDisplay(slug)}
                    </Link>
                ),
            )}
        </nav>
    );
}

/** App instance bound to LAUNCHED_STATES — the only form product code should mount.
 *  currentState null = the /gyms chooser (both pills travel, filters carried). */
export default function StateDirectorySwitch({ currentState }: { currentState: StateSlug | null }) {
    return <StateDirectorySwitchFor states={LAUNCHED_STATES as readonly StateSlug[]} currentState={currentState} />;
}
